import * as React from 'react';
import {Form, Tag} from 'antd';
import {FormComponentProps} from 'antd/lib/form';

import {ErrorResponse, RentalItem} from '../api/dtos/dtos';
import locales from '../locales';

import {ApplicationContextConsumer, bindLoadingHandler, LoadingOverlayHandler} from '../app/ApplicationContext';
import ItemDetailsAlert from '../item-details/ItemDetailsAlert';
import {ErrorAlert} from '../common/ErrorAlert';
import {SelectValue} from 'antd/lib/select';
import {getAmountTypeOptions, getPriceForItem, isAbrasionSettlement} from '../common/AmountType';
import HiddenFormItem from '../common/form-items/HiddenFormItem';
import SelectFormItem from '../common/form-items/SelectFormItem';
import NumberFormItem from '../common/form-items/NumberFormItem';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import {numberGroupFormatter} from '../utils/number-utils';
import {gteOrEmptyValidator, lteOrEmptyValidator} from '../utils/form-utils';
import {ItemType} from "../item-details/itemType";
import TextAreaFormItem from '../common/form-items/TextAreaFormItem';

interface RentalItemFormProps extends FormComponentProps {
  rentalItem?: RentalItem;
  onFormSubmit: (rentalItem: RentalItem) => void;
  onFormCancel: () => void;
}

const FIELD_ITEM_ID = 'itemId';
const FIELD_COMMENT = 'comment';
const FIELD_CAUTION = 'caution';
const FIELD_AMOUNT_TYPE = 'amountType';
const FIELD_PRICE = 'price';
const FIELD_RENTAL_ABRASION_MM = 'rentalAbrasionMm';
const FIELD_RENTAL_OPERATION_HOURS = 'rentalOperationHours';
const FIELD_RETURNAL_ABRASION_MM = 'returnalAbrasionMm';
const FIELD_RETURNAL_OPERATION_HOURS = 'returnalOperationHours';

interface RentalItemFormState {
  error?: ErrorResponse;
  isAbrasionBased: boolean;
}

class RentalItemForm extends React.Component<RentalItemFormProps, RentalItemFormState> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RentalItemFormProps) {
    super(props);
    const {rentalItem} = this.props;

    this.state = {
      isAbrasionBased: isAbrasionSettlement(rentalItem && rentalItem.amountType)
    };
  }

  public render() {
    const {form, rentalItem} = this.props;
    const item = rentalItem ? rentalItem.item : null;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Form>
          <HiddenFormItem
            form={form}
            fieldId={FIELD_ITEM_ID}
            label={locales.item}
            initialValue={item ? item.id : null}
            required={true}
          >
            {item && <ItemDetailsAlert item={item}/>}
          </HiddenFormItem>

          <NumberFormItem
            label={locales.caution}
            fieldId={FIELD_CAUTION}
            form={form}
            initialValue={rentalItem && rentalItem.caution}
            required={false}
            min={0}
            precision={0}
            formatter={numberGroupFormatter}
          >
            <> {locales.app_currency}</>
          </NumberFormItem>

          <TextAreaFormItem
            label={locales.comment}
            form={form}
            fieldId={FIELD_COMMENT}
            initialValue={rentalItem && rentalItem.comment}
            required={false}
            maxLength={255}
          />

          <SelectFormItem
            form={form}
            fieldId={FIELD_AMOUNT_TYPE}
            label={locales.settlement}
            initialValue={rentalItem ? rentalItem.amountType : null}
            options={item ? getAmountTypeOptions(item.itemType) : []}
            onSelect={this.onAmountTypeSelect}
            required={true}
          />

          <NumberFormItem
            label={locales.net_price}
            fieldId={FIELD_PRICE}
            form={form}
            initialValue={rentalItem ? rentalItem.price : null}
            required={true}
            min={0}
            precision={0}
            formatter={numberGroupFormatter}
          >
            <> {locales.app_currency}</>
          </NumberFormItem>

          {item && (item.itemType === ItemType.DEVICE) && this.renderExtraDataFields()}

          {this.props.children}
          {this.state.error && <ErrorAlert style={{margin: '16px 0'}} error={this.state.error}/>}

          <SubmitCancelFormItem
            onSubmit={this.onFormSubmit}
            onCancel={this.props.onFormCancel}/>
        </Form>
      </>
    );
  }

  private renderExtraDataFields = (): React.ReactNode => {
    const {isAbrasionBased} = this.state;
    return isAbrasionBased ? this.renderExtraAbrasionFields() : this.renderExtraOperationalHoursFields();
  };

  private renderExtraAbrasionFields = (): React.ReactNode => {
    const {form, form: {getFieldValue}, rentalItem} = this.props;
    return (
      <>
        <NumberFormItem
          label={locales.abrasion_dimension}
          fieldId={FIELD_RENTAL_ABRASION_MM}
          form={form}
          initialValue={rentalItem ? rentalItem.rentalAbrasionMm : null}
          required={true}
          min={0}
          step={0.1}
          precision={1}
          formatter={numberGroupFormatter}
        >
          <> {locales.mm} <br/><Tag color="blue">{locales.at_rental}</Tag></>
        </NumberFormItem>

        <NumberFormItem
          label={locales.abrasion_dimension}
          fieldId={FIELD_RETURNAL_ABRASION_MM}
          form={form}
          initialValue={rentalItem ? rentalItem.returnalAbrasionMm : null}
          required={false}
          min={0}
          step={0.1}
          precision={1}
          rules={[{
            validator: (rule, value, callback) => {
              lteOrEmptyValidator(value, getFieldValue(FIELD_RENTAL_ABRASION_MM), callback);
            },
            message: locales.cant_be_greater_than_rented_value
          }]}
          formatter={numberGroupFormatter}
        >
          <> {locales.mm} <br/><Tag color="purple">{locales.at_returnal}</Tag></>
        </NumberFormItem>
      </>
    );
  };

  private renderExtraOperationalHoursFields = (): React.ReactNode => {
    const {form, form: {getFieldValue}, rentalItem} = this.props;
    return (
      <>
        <NumberFormItem
          label={locales.operationHours}
          fieldId={FIELD_RENTAL_OPERATION_HOURS}
          form={form}
          initialValue={rentalItem ? rentalItem.rentalOperationHours : null}
          required={false}
          min={0}
          step={0.1}
          precision={1}
        >
          <> {locales.hours} <br/><Tag color="blue">{locales.at_rental}</Tag></>
        </NumberFormItem>

        <NumberFormItem
          label={locales.operationHours}
          fieldId={FIELD_RETURNAL_OPERATION_HOURS}
          form={form}
          initialValue={rentalItem ? rentalItem.returnalOperationHours : null}
          required={false}
          min={0}
          step={0.1}
          precision={1}
          rules={[{
            validator: (rule, value, callback) => {
              gteOrEmptyValidator(value, getFieldValue(FIELD_RENTAL_OPERATION_HOURS), callback);
            },
            message: locales.cant_be_less_than_rented_value
          }]}
        >
          <> {locales.hours} <br/><Tag color="purple">{locales.at_returnal}</Tag></>
        </NumberFormItem>
      </>
    );
  };

  private onAmountTypeSelect = (value: SelectValue) => {
    const val = String(value);
    const {form, rentalItem} = this.props;
    const item = rentalItem && rentalItem.item;
    if (item) {
      form.setFieldsValue({[FIELD_PRICE]: getPriceForItem(item, val)});
    }
    this.setState({
      isAbrasionBased: isAbrasionSettlement(val),
    });
  };

  private onFormSubmit = () => {
    const {form, onFormSubmit, rentalItem} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (err) {
        return;
      }

      const amountType = values[FIELD_AMOUNT_TYPE];
      const abrasionSettlement: boolean = isAbrasionSettlement(amountType);

      const newRentalItem: RentalItem = {
        ...rentalItem!,
        caution: values[FIELD_CAUTION],
        comment: values[FIELD_COMMENT],
        price: values[FIELD_PRICE],
        amountType,
        rentalAbrasionMm: abrasionSettlement ? values[FIELD_RENTAL_ABRASION_MM] : undefined,
        returnalAbrasionMm: abrasionSettlement ? values[FIELD_RETURNAL_ABRASION_MM] : undefined,
        rentalOperationHours: abrasionSettlement ? undefined : values[FIELD_RENTAL_OPERATION_HOURS],
        returnalOperationHours: abrasionSettlement ? undefined : values[FIELD_RETURNAL_OPERATION_HOURS],
      };

      onFormSubmit(newRentalItem);
    });
  };

}

export default Form.create<RentalItemFormProps>()(RentalItemForm);




// WEBPACK FOOTER //
// ./src/rental-details/RentalItemForm.tsx